// ─── Google Drive link-field (drive-connector Phase 4) ──────────────────────
//
// A source-adapter UI beside the upload dropbox: the user pastes a view-only
// Google Drive / Docs / Sheets / Slides share link, and the field POSTs it to
// the landed POST /api/upload/drive-link route (Phase 3). That route is a
// source adapter over the SAME pipeline a browser upload traverses and returns
// the IDENTICAL UploadResponse shape (INV-DRIVE-5), so on success we hand the
// returned jobs into the SAME job-progress UI (trackJob → render → poll) that
// uploadFiles()/retryUpload() use — no new job state, no new queue.
//
// Drive-side failures come back as typed 4xx (the closed _DRIVE_ERROR_HTTP_STATUS
// table in routes_upload.py maps each DriveErrorCode → a distinct status); we
// map those statuses to friendly inline guidance. Client-side we reject only
// what is OBVIOUSLY non-Drive (an unparseable URL or a non-google.com host) —
// the server's typed grammar validation (INV-DRIVE-4) is the authority.
//
// Epic: docs/epics/active/drive-connector-ingest-260915/main.md Phase 4.
import { API, authFetch } from './globals-boot.mjs';
import { jobState } from './job-state.js';
import { startPolling } from './polling.mjs';
const log = (typeof window !== 'undefined' && window.adatool?.log) || {
    info: () => { }, warn: () => { }, error: () => { }, debug: () => { },
};
// Friendly inline guidance keyed by the HTTP status the drive-link route
// returns. Each status is the closed mapping of one DriveErrorCode
// (routes_upload.py:_DRIVE_ERROR_HTTP_STATUS): 400 BAD_LINK, 404 NOT_FOUND,
// 415 UNSUPPORTED_TYPE, 403 EXPORT_RESTRICTED, 413 EXPORT_TOO_LARGE,
// 429 RATE_LIMITED, 502 DRIVE_ERROR, 503 unconfigured. The message names the
// user-side fix (mirrors the DriveErrorCode docstrings) so the field is
// self-service — no need to read the raw server detail.
const _DRIVE_ERROR_MESSAGE = {
    400: 'That doesn’t look like a Google Drive, Docs, Sheets, or Slides link. Paste the share link from Google.',
    403: 'The file’s owner has disabled download/copy, so it can’t be imported. Ask the owner to allow downloads, or download the file and upload it directly.',
    404: 'We couldn’t open that file. Set link sharing to “Anyone with the link — Viewer” and try again.',
    413: 'That file is too large to import by link. Download it from Google as an Office file and upload it directly.',
    415: 'Only Google Docs, Sheets, and Slides can be imported by link. For a stored PowerPoint, Word, Excel, or PDF file, download it and upload the file directly.',
    429: 'Too many Google Drive requests right now. Please wait a moment and try again.',
    502: 'We couldn’t reach Google Drive. Please try again.',
    503: 'Google Drive import isn’t available on this deployment yet.',
};
/** Friendly message for a drive-link error status; falls back to the server
 *  detail (if any) then a generic message. */
function _friendlyDriveError(status, serverDetail) {
    const mapped = _DRIVE_ERROR_MESSAGE[status];
    if (mapped)
        return mapped;
    if (serverDetail)
        return serverDetail;
    return `Google Drive import failed (error ${status}). Please try again.`;
}
/** Obviously-non-Drive client-side pre-check. Returns true only for links the
 *  server would certainly reject as BAD_LINK — an unparseable URL, or a host
 *  that is not a google.com subdomain. The server's grammar validation
 *  (INV-DRIVE-4) is the authority for everything else; we do not attempt to
 *  parse the fileId here. */
function _isObviouslyNotDrive(raw) {
    let u;
    try {
        u = new URL(raw);
    }
    catch {
        return true;
    }
    if (u.protocol !== 'https:' && u.protocol !== 'http:')
        return true;
    const host = u.hostname.toLowerCase();
    return !(host === 'google.com' || host.endsWith('.google.com'));
}
// Best-effort reads of the shared remediation-pref window bridges (set by
// init.ts). A Drive ingest accepts the SAME optional prefs a browser upload
// does (DriveLinkRequest), so the options rail applies identically — this keeps
// the two entry paths behaviourally parallel. Each read is guarded because the
// options rail may not be initialised (e.g. in a seam test).
function _smComputeUploadDiff() {
    try {
        if (typeof window !== 'undefined' && typeof window._adaSmComputeUploadDiff === 'function') {
            return window._adaSmComputeUploadDiff();
        }
    }
    catch { /* best-effort; sm rail may not be initialised */ }
    return {};
}
function _userContextAltText() {
    try {
        if (typeof window !== 'undefined' && typeof window._adaUserContextForUpload === 'function') {
            return window._adaUserContextForUpload();
        }
    }
    catch { /* best-effort; user-context UI may not be initialised */ }
    return null;
}
function _audienceLevel() {
    try {
        if (typeof window !== 'undefined' && typeof window._adaAudienceLevelForUpload === 'function') {
            return window._adaAudienceLevelForUpload();
        }
    }
    catch { /* best-effort; audience-level UI may not be initialised */ }
    return null;
}
function _render() {
    if (typeof window !== 'undefined' && typeof window._adaRender === 'function') {
        window._adaRender();
    }
}
function _showError(errEl, msg) {
    if (!errEl)
        return;
    errEl.textContent = msg;
    errEl.hidden = false;
}
function _clearError(errEl) {
    if (!errEl)
        return;
    errEl.textContent = '';
    errEl.hidden = true;
}
/**
 * Submit a Drive share link to POST /api/upload/drive-link and, on success,
 * enter the SAME job-progress UI a browser upload uses.
 *
 * @param rawUrl the untrusted user-supplied share link
 * @param errEl inline error region (role=alert)
 * @returns true when a job was created and the job UI entered
 */
export async function submitDriveLink(rawUrl, errEl) {
    const url = rawUrl.trim();
    if (!url) {
        _showError(errEl, 'Paste a Google Drive, Docs, Sheets, or Slides share link.');
        return false;
    }
    if (_isObviouslyNotDrive(url)) {
        _showError(errEl, _DRIVE_ERROR_MESSAGE[400] ?? 'That doesn’t look like a Google Drive link.');
        return false;
    }
    _clearError(errEl);
    const body = { url };
    // Attach the shared remediation prefs so a Drive ingest honours the same
    // options rail a browser upload does (parity with uploadFiles()).
    try {
        const diff = _smComputeUploadDiff();
        if (diff && Object.keys(diff).length > 0)
            body.sm_prefs_diff = JSON.stringify(diff);
    }
    catch { /* best-effort; sm rail may not be initialised */ }
    const uctx = _userContextAltText();
    if (uctx)
        body.user_context_alt_text = uctx;
    const aud = _audienceLevel();
    if (aud)
        body.audience_level = aud;
    let resp;
    try {
        resp = await authFetch(`${API}/api/upload/drive-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    }
    catch (e) {
        log.error('[ada-tool] drive-link request error:', e instanceof Error ? e.message : String(e));
        _showError(errEl, 'We couldn’t reach the server. Please try again.');
        return false;
    }
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const detail = (data && data.detail);
        const serverDetail = typeof detail === 'string'
            ? detail
            : (detail && typeof detail.message === 'string'
                ? detail.message
                : '');
        const friendly = _friendlyDriveError(resp.status, serverDetail);
        log.info('[ada-tool] drive-link reject status=%d detail=%s', resp.status, serverDetail);
        _showError(errEl, friendly);
        return false;
    }
    const data = await resp.json().catch(() => ({ jobs: [] }));
    const jobs = Array.isArray(data.jobs)
        ? data.jobs
        : [];
    if (jobs.length === 0) {
        log.warn('[ada-tool] drive-link returned no jobs');
        _showError(errEl, 'The link was accepted but no document was created. Please try again.');
        return false;
    }
    for (const job of jobs) {
        log.info('[ada-tool] drive-link ingest job:', job.id, job.filename);
        // TrackedJob is a loose superset of the server JobSummary; cast is safe.
        jobState.trackJob(job);
    }
    jobState.saveJobIds();
    _render();
    startPolling();
    return true;
}
/**
 * Wire the Drive link-field: the input (#drive-link-input), submit button
 * (#drive-link-submit), and inline error region (#drive-link-error). Enter and
 * button-click both submit; the button is disabled while a request is in
 * flight so a double-submit cannot create two jobs. On success the input is
 * cleared. No-op when the field is absent (e.g. a page without the SPA shell).
 */
export function initDriveLink() {
    const input = document.getElementById('drive-link-input');
    const button = document.getElementById('drive-link-submit');
    const errEl = document.getElementById('drive-link-error');
    if (!input || !button)
        return;
    let inFlight = false;
    const doSubmit = () => {
        void (async () => {
            if (inFlight)
                return;
            inFlight = true;
            button.disabled = true;
            try {
                const ok = await submitDriveLink(input.value, errEl);
                if (ok)
                    input.value = '';
            }
            finally {
                inFlight = false;
                button.disabled = false;
            }
        })();
    };
    button.addEventListener('click', doSubmit);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            doSubmit();
        }
    });
    // Clear a stale error the moment the user edits the link.
    input.addEventListener('input', () => _clearError(errEl));
}
